import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import { useGetIdentity, useNotify } from 'react-admin';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { MAX_PERSON_NAME_LENGTH, fetchOwnProfile, saveOwnProfile } from './ownProfileApi';

/**
 * „Mój profil” w menu użytkownika — imię i nazwisko własnego konta
 * (decyzja użytkownika 07.10.2026: w API TMS ani w telematyce ich nie ma,
 * więc wpisuje je sam użytkownik).
 *
 * Z imienia i nazwiska korzystają: pasek i menu użytkownika (zamiast
 * loginu), osoba kontaktowa w ofercie TIMOCOM i podpis zapytania do
 * przewoźnika. Bez nich wszędzie zostaje to, co było — „tylko jeśli jest
 * skonfigurowane”.
 *
 * Okno zamiast pól w samym menu: menu przeskakuje po literach do pozycji,
 * a pole tekstowe w nim walczy o klawiaturę. Po zapisie odświeżamy
 * tożsamość, więc pasek pokazuje nowe imię bez przeładowania strony.
 */
export const OwnProfileMenuItem = () => {
  const notify = useNotify();
  const { refetch } = useGetIdentity();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [login, setLogin] = useState<string>('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const openDialog = () => {
    setOpen(true);
    setError(null);
    setLoading(true);
    fetchOwnProfile()
      .then((p) => {
        setLogin(p.username);
        setFirstName(p.firstName ?? '');
        setLastName(p.lastName ?? '');
      })
      .catch((e) => setError(toErrorMessage(e)))
      .finally(() => setLoading(false));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveOwnProfile({ firstName, lastName });
      notify('Zapisano imię i nazwisko', { type: 'success' });
      setOpen(false);
      await refetch?.();
    } catch (e) {
      setError(toErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const tooLong = firstName.trim().length > MAX_PERSON_NAME_LENGTH || lastName.trim().length > MAX_PERSON_NAME_LENGTH;

  return (
    <>
      <MenuItem onClick={openDialog}>
        <ListItemIcon>
          <PersonOutlineIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Mój profil</ListItemText>
      </MenuItem>
      <Dialog
        open={open}
        onClose={() => !saving && setOpen(false)}
        fullWidth
        maxWidth="xs"
        // Klawisze zostają w oknie — menu pod spodem przeskakiwało po literach.
        onKeyDown={(e) => e.stopPropagation()}
      >
        <DialogTitle>Mój profil</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Imię i nazwisko pokazujemy zamiast loginu w pasku aplikacji, jako osobę kontaktową w ofercie TIMOCOM
            i w podpisie zapytania do przewoźnika. Bez nich zostaje login.
          </DialogContentText>
          <Stack spacing={2}>
            <TextField
              autoFocus
              size="small"
              label="Imię"
              value={firstName}
              disabled={loading || saving}
              onChange={(e) => setFirstName(e.target.value)}
              slotProps={{ htmlInput: { maxLength: MAX_PERSON_NAME_LENGTH } }}
            />
            <TextField
              size="small"
              label="Nazwisko"
              value={lastName}
              disabled={loading || saving}
              onChange={(e) => setLastName(e.target.value)}
              slotProps={{ htmlInput: { maxLength: MAX_PERSON_NAME_LENGTH } }}
            />
          </Stack>
          {login ? (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
              Login: {login} — zmienia go administrator.
            </Typography>
          ) : null}
          {error ? (
            <Typography variant="body2" color="error" sx={{ mt: 1 }} role="alert">
              {error}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" disabled={saving} onClick={() => setOpen(false)}>
            Anuluj
          </Button>
          <Button variant="contained" disabled={loading || saving || tooLong} onClick={() => void save()}>
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
